"use client";

import { ArrowClockwiseIcon, ClockIcon } from "@phosphor-icons/react";
import Image from "next/image";
import { type ReactElement, useState } from "react";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useTranslation } from "@/i18n/client";
import { cn } from "@/utils/shared";
import { MaintenanceIllustration } from "../maintenance-illustration/maintenance-illustration";

const LOGO_WIDTH = 176;
const LOGO_HEIGHT = 36;
const STATUS_ICON_SIZE = 20;

interface MaintenanceScreenProps {
  lng: string;
}

export function MaintenanceScreen({ lng }: MaintenanceScreenProps): ReactElement {
  const { t } = useTranslation("maintenance", lng, { lng });
  const [isReloading, setIsReloading] = useState(false);

  function handleRetry(): void {
    setIsReloading(true);
    window.location.reload();
  }

  return (
    <div className="flex min-h-dvh flex-col items-center bg-gradient-to-br from-primary-100 via-neutral-50 to-secondary-100/50 px-4 py-6 selection:bg-primary-200 selection:text-primary-900 sm:py-12">
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-6 sm:gap-8">
        <Image
          src="/images/logos/evaluar-main.svg"
          alt={t("logoAlt")}
          width={LOGO_WIDTH}
          height={LOGO_HEIGHT}
          priority={true}
          className="h-auto w-36 sm:w-44"
        />
        <section className="flex w-full max-w-[560px] flex-col items-center rounded-2xl bg-neutral-0 px-5 py-8 text-center shadow-[0_24px_48px_-24px_hsl(var(--primary-900)/0.22)] sm:px-10 sm:py-10">
          <MaintenanceIllustration />
          <h1 className="mt-6">
            <Typography
              variant="heading 1"
              className="text-balance text-2xl leading-8 font-bold tracking-[-0.02em] text-primary-900 sm:text-[28px] sm:leading-10"
            >
              {t("title")}
            </Typography>
          </h1>
          <Typography variant="body 1" className="mt-3 max-w-[46ch] text-pretty text-neutral-700">
            {t("description")}
          </Typography>
          <div className="mt-6 flex w-full items-start gap-3 rounded-xl bg-primary-100 p-4 text-left">
            <ClockIcon
              size={STATUS_ICON_SIZE}
              weight="duotone"
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-primary-600"
            />
            <div className="flex flex-col gap-1">
              <Typography variant="label 3" className="text-primary-900">
                {t("status")}
              </Typography>
              <Typography variant="body 2" className="text-pretty text-neutral-700">
                {t("statusDetail")}
              </Typography>
            </div>
          </div>
          <Button
            size="lg"
            className="mt-8 w-full sm:w-auto sm:min-w-52"
            onClick={handleRetry}
            disabled={isReloading}
            aria-busy={isReloading}
          >
            <ArrowClockwiseIcon
              aria-hidden="true"
              className={cn(isReloading && "motion-safe:animate-spin")}
            />
            {t("retry")}
          </Button>
          <Typography variant="body 2" className="mt-4 text-neutral-700">
            {t("thanks")}
          </Typography>
        </section>
      </div>
      <footer className="mt-6 sm:mt-8">
        <Typography variant="body 2" className="text-center text-neutral-700">
          {t("tagline")}
        </Typography>
      </footer>
    </div>
  );
}
